import { useCallback, useRef, useState, type ReactNode } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Copy,
  Download,
  Film,
  Layers,
  Loader2,
  MoreHorizontal,
  Scissors,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProBadge } from "@/components/ProBadge";
import { DeleteListItemDialog } from "@/components/lists/DeleteListItemDialog";
import ProFeatureDialog from "@/components/ProFeatureDialog";
import { ProcessingStepper } from "@/components/ProcessingStepper";
import { OriginalVideoDownloadDialog } from "@/components/editor/OriginalVideoDownloadButton";
import { getProjectStatusLabel } from "@/constants/project-status";
import { useProjectPolling, useLibraryPolling } from "@/hooks/use-polling";
import { useDeleteProject } from "@/hooks/use-delete-project";
import { useDeleteLibrary } from "@/hooks/use-delete-library";
import { MyProject } from "@/generated/graphql";
import { formatDuration } from "@/lib/format";
import { formatAiredDay } from "@/lib/format/airedDay";
import { resolveOriginalVideoDownloadUrl } from "@/lib/downloads/resolveOriginalVideoDownloadUrl";
import {
  buildListCardMenu,
  resolveListCardAction,
  type ListCardMenuItem,
} from "@/lib/library/listCardActions";
import type { LibraryOrigin } from "@/lib/library/classifyLibraryOrigin";
import { useHasFeature } from "@/lib/useHasFeature";
import { cn } from "@/lib/utils";

/** A project, or a library passed through the same card. */
export type ProjectCardItem = MyProject & {
  original_video_url?: string | null;
  versions_count?: number | null;
  youtube_channels?: Array<{ id: string; name: string; thumbnail?: string | null }> | null;
};

const formatEtaTime = (etaSeconds: number | null | undefined): string => {
  if (!etaSeconds || etaSeconds <= 0) return "";
  const minutes = Math.floor(etaSeconds / 60);
  const seconds = Math.round(etaSeconds % 60);
  if (minutes > 0 && seconds > 0) return `${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
};

/** Menu items that open a dialog: the menu must not pull focus back to its trigger. */
const OPENS_DIALOG: ReadonlySet<ListCardMenuItem> = new Set(['duplicate', 'downloadOriginal', 'delete']);

interface ProjectCardProps {
  project: ProjectCardItem;
  type?: 'project' | 'library';
  origin?: LibraryOrigin;
  onDuplicate?: (project: ProjectCardItem) => void;
}

/**
 * A /projects or /library card. The whole card is one link (a sibling overlay,
 * so the buttons are not nested in it); the main button names where it leads
 * ("Ver cortes" / "Abrir no editor") and every other action is in the "⋯"
 * menu, visible without hovering. Below `sm` it is a list row.
 */
export const ProjectCard = ({ project, type = 'project', origin, onDuplicate }: ProjectCardProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const hasProAccess = useHasFeature('pro_features');
  const isLibrary = type === 'library';
  const statusCode = project.curation_status;

  // Real-time updates while the video is processing.
  const pollingEnabled = !['success', 'failure', 'expired'].includes(statusCode ?? '');
  const projectPolling = useProjectPolling(project.id, { enabled: !isLibrary && pollingEnabled, interval: 5000 });
  const libraryPolling = useLibraryPolling(project.id, { enabled: isLibrary && pollingEnabled, interval: 5000 });
  const { data: polledProject, isFinished, finishedState } = isLibrary ? libraryPolling : projectPolling;
  const status = isFinished
    ? (finishedState === 'success' ? 'success' : 'failure')
    : (polledProject?.curation_status || statusCode);
  const name = polledProject?.name || project.name || '';
  const thumbnail = polledProject?.thumbnail_url || project.thumbnail_url || undefined;
  const etaTime = formatEtaTime(polledProject?.eta_seconds);

  const [thumbnailError, setThumbnailError] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [originalOpen, setOriginalOpen] = useState(false);
  const [proDownloadOpen, setProDownloadOpen] = useState(false);
  const openingDialogRef = useRef(false);

  const { deleteProject, loading: deletingProject } = useDeleteProject();
  const { deleteLibrary, loading: deletingLibrary } = useDeleteLibrary();
  const deleting = isLibrary ? deletingLibrary : deletingProject;

  const originalDownloadUrl = resolveOriginalVideoDownloadUrl({
    originalVideoUrl: project.original_video_url,
    cutVideoUrl: project.cut_video_url,
    baseVideoUrl: project.base_video_url,
    compressedBaseVideoUrl: project.compressed_base_video_url,
  });

  const action = resolveListCardAction({
    type, id: project.id, status, processingMode: project.processing_mode,
  });
  const menuItems = buildListCardMenu({
    type,
    status,
    isPublic: Boolean(project.public),
    canDuplicate: Boolean(onDuplicate),
    hasOriginal: Boolean(originalDownloadUrl),
  });

  const handleThumbnailError = useCallback(() => setThumbnailError(true), []);

  const handleConfirmDelete = async () => {
    if (isLibrary) await deleteLibrary(project.id);
    else await deleteProject(project.id);
    setShowDeleteDialog(false);
  };

  const handleMenu = (item: ListCardMenuItem) => {
    if (item === 'clips') navigate(`/${type}/${project.id}/clips`);
    else if (item === 'editor') navigate(`/${type}/${project.id}/edit`);
    else if (item === 'duplicate') onDuplicate?.(project);
    else if (item === 'downloadOriginal') {
      if (hasProAccess) setOriginalOpen(true);
      else setProDownloadOpen(true);
    } else if (item === 'delete') setShowDeleteDialog(true);
  };

  const channel = project.youtube_channels?.[0];
  const channelName = channel?.name?.split(' | ')[0]?.trim();
  const dateLabel = formatAiredDay(project.createdAt, i18n.language);
  const showPicture = Boolean(thumbnail) && !thumbnailError;

  const mainLabel: Record<string, { icon: ReactNode; label: string }> = {
    clips: { icon: <Film className="h-4 w-4" />, label: t('projectCard.openClips') },
    editor: { icon: <Scissors className="h-4 w-4" />, label: t('projectCard.openEditor') },
    processing: { icon: <Loader2 className="h-4 w-4 animate-spin" />, label: t('projectCard.processing') },
    failed: { icon: <AlertCircle className="h-4 w-4" />, label: t('projectCard.seeFailure') },
  };
  const main = mainLabel[action.kind];

  const menuLabels: Record<ListCardMenuItem, { testId: string; icon: ReactNode; label: string; extra?: ReactNode }> = {
    clips: { testId: 'list-card-menu-clips', icon: <Film />, label: t('projectCard.menu.clips') },
    editor: { testId: 'list-card-menu-editor', icon: <Scissors />, label: t('projectCard.menu.editor') },
    duplicate: { testId: 'list-card-menu-duplicate', icon: <Copy />, label: t('projectCard.menu.duplicate') },
    downloadOriginal: {
      testId: 'list-card-menu-download-original',
      icon: <Download />,
      label: t('projectCard.menu.downloadOriginal'),
      extra: hasProAccess ? undefined : <ProBadge className="ml-auto px-1.5 py-0 text-[9px] leading-4" />,
    },
    delete: { testId: 'list-card-menu-delete', icon: <Trash2 />, label: t('projectCard.menu.delete') },
  };

  return (
    <>
      <article
        data-testid="list-card"
        className="group relative flex gap-3 rounded-xl border border-border bg-card p-2 transition-colors hover:border-primary/40 sm:flex-col sm:gap-0 sm:overflow-hidden sm:p-0"
      >
        {action.href && (
          <RouterLink
            to={action.href}
            aria-label={name}
            data-testid="list-card-link"
            className="absolute inset-0 z-[1] rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        )}

        <div className="relative aspect-video w-32 shrink-0 overflow-hidden rounded-lg bg-black sm:w-full sm:rounded-none">
          {showPicture ? (
            <img src={thumbnail} alt="" className="h-full w-full object-cover" onError={handleThumbnailError} />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-secondary to-card text-[10px] text-muted-foreground sm:text-xs">
              {channel?.thumbnail ? (
                <img src={channel.thumbnail} alt="" className="hidden h-12 w-12 rounded-full object-cover sm:block" />
              ) : null}
              {t('projectCard.noPreview')}
            </div>
          )}

          {isLibrary && origin && (
            <span
              data-testid="library-origin-badge"
              className={cn(
                'absolute left-2 top-2 z-[2] hidden items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm sm:inline-flex',
                origin === 'community'
                  ? 'bg-background/85 text-foreground ring-1 ring-white/15 backdrop-blur-sm'
                  : 'bg-primary text-primary-foreground',
              )}
            >
              {origin === 'community' ? <Users className="h-3 w-3" /> : <Upload className="h-3 w-3" />}
              {origin === 'community' ? t('library.originCommunity') : t('library.originUploaded')}
            </span>
          )}

          {status === 'success' && (project.versions_count ?? 1) > 1 && (
            <span
              data-testid="list-card-versions"
              title={t('projectCard.versionsTitle', { count: project.versions_count ?? 0 })}
              className="absolute left-1 top-1 z-[2] inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-1.5 py-0.5 text-[10px] font-bold text-black sm:left-auto sm:right-2 sm:top-2 sm:px-2"
            >
              <Layers className="h-3 w-3" />
              <span className="sm:hidden">{project.versions_count}</span>
              <span className="hidden sm:inline">{t('projectCard.versions', { count: project.versions_count ?? 0 })}</span>
            </span>
          )}

          {status === 'success' && project.duration_seconds ? (
            <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-semibold text-white sm:bottom-2 sm:right-2 sm:text-[11px]">
              {formatDuration(project.duration_seconds)}
            </span>
          ) : null}

          {action.kind === 'processing' && (
            <div
              data-testid="list-card-processing"
              className="pointer-events-none absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/90 via-black/45 to-black/20 p-2 sm:p-2.5"
            >
              <ProcessingStepper status={status ?? ''} className="mb-1.5 hidden sm:flex" />
              <span className="text-[11px] font-medium leading-tight text-white sm:text-xs">
                {getProjectStatusLabel(status ?? '')}
              </span>
              {etaTime && (
                <span className="mt-0.5 text-[10px] text-white/65 sm:text-[11px]">
                  {t('projectCard.estimate', { time: etaTime })}
                </span>
              )}
            </div>
          )}

          {action.kind === 'failed' && (
            <div
              data-testid="list-card-failed"
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 bg-red-950/85 p-2 text-center"
            >
              <AlertCircle className="h-5 w-5 text-red-300 sm:h-7 sm:w-7" />
              <span className="text-[11px] font-semibold text-white sm:text-[13px]">{t('projectCard.failedTitle')}</span>
              <span className="hidden text-xs text-white/70 sm:block">{t('projectCard.failedDescription')}</span>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col sm:p-3">
          <h3 className="line-clamp-2 pr-8 text-[13px] font-semibold leading-snug sm:min-h-[2.6em] sm:pr-0 sm:text-sm">
            {name}
          </h3>

          <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground sm:mt-1.5 sm:text-xs">
            {isLibrary ? (
              <>
                {channel?.thumbnail ? (
                  <img src={channel.thumbnail} alt="" className="hidden h-4 w-4 shrink-0 rounded-full object-cover sm:block" />
                ) : null}
                <span className="truncate">
                  {channelName || (origin === 'community' ? '' : t('projectCard.uploadedFile'))}
                </span>
                {dateLabel && <span aria-hidden="true">·</span>}
              </>
            ) : null}
            {dateLabel && <span className="shrink-0">{dateLabel}</span>}
            {!isLibrary && project.duration_seconds ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="shrink-0">{formatDuration(project.duration_seconds)}</span>
              </>
            ) : null}
          </div>

          <div data-testid="list-card-stats" className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground sm:mt-1 sm:text-xs">
            {status === 'success' ? (
              <>
                <span>{t('projectCard.clipsCount', { count: project.clips_count ?? 0 })}</span>
                <span aria-hidden="true">·</span>
                <span>{t('projectCard.exportedCount', { count: project.exported_clips_count ?? 0 })}</span>
              </>
            ) : action.kind === 'failed' ? (
              <span className="font-semibold text-red-400">{t('projectCard.statusFailed')}</span>
            ) : (
              <span>{t('projectCard.statusProcessing')}</span>
            )}
          </div>

          <div className="absolute right-1 top-1 z-[2] flex gap-2 sm:relative sm:right-auto sm:top-auto sm:mt-auto sm:pt-3">
            {main && action.href && (
              <Button
                asChild
                variant="secondary"
                size="sm"
                className={cn(
                  'hidden h-9 flex-1 gap-2 font-semibold sm:inline-flex',
                  action.kind === 'processing' && 'text-muted-foreground',
                )}
              >
                <RouterLink to={action.href} data-testid="list-card-main-action">
                  {main.icon}
                  {main.label}
                </RouterLink>
              </Button>
            )}
            {menuItems.length > 0 && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="list-card-more-actions"
                    aria-label={t('projectCard.moreActions')}
                    className="h-8 w-8 shrink-0 p-0 text-muted-foreground sm:h-9 sm:w-9 sm:border sm:border-border sm:text-foreground"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="z-[100] min-w-[14rem]"
                  onCloseAutoFocus={(event) => {
                    if (openingDialogRef.current) {
                      openingDialogRef.current = false;
                      event.preventDefault();
                    }
                  }}
                >
                  {menuItems.map((item) => {
                    const { testId, icon, label, extra } = menuLabels[item];
                    return [
                      item === 'delete' && menuItems.length > 1
                        ? <DropdownMenuSeparator key="separator" />
                        : null,
                      <DropdownMenuItem
                        key={item}
                        data-testid={testId}
                        className={cn(
                          'cursor-pointer gap-2 [&_svg]:h-4 [&_svg]:w-4',
                          item === 'delete' && 'text-destructive focus:text-destructive',
                        )}
                        onSelect={() => {
                          if (OPENS_DIALOG.has(item)) openingDialogRef.current = true;
                          handleMenu(item);
                        }}
                      >
                        {icon}
                        <span>{label}</span>
                        {extra}
                      </DropdownMenuItem>,
                    ];
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </article>

      <DeleteListItemDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleConfirmDelete}
        deleting={deleting}
        warnAboutClips={status !== 'failure'}
      />
      {originalOpen && (
        <OriginalVideoDownloadDialog
          open={originalOpen}
          onOpenChange={setOriginalOpen}
          sourceUrl={originalDownloadUrl}
          sourceDuration={project.duration_seconds}
          showExportHint={false}
          i18nPrefix="projectCard.originalVideoDownload"
        />
      )}
      <ProFeatureDialog
        open={proDownloadOpen}
        onOpenChange={setProDownloadOpen}
        onUpgrade={() => navigate('/plans')}
        title={t('projectCard.originalVideoDownload.proTitle')}
        body={t('projectCard.originalVideoDownload.proBody')}
        dismissLabel={t('projectCard.originalVideoDownload.proDismiss')}
        upgradeLabel={t('projectCard.originalVideoDownload.proUpgrade')}
        testIdPrefix="project-original-download-pro"
      />
    </>
  );
};
